/**
 * Game load analytics: the `GameLoadFinish` event.
 *
 * HOW TO USE IT
 * Wrap the await you want to see on the event. That is the whole API:
 *
 *   await measureLoadPhase('replicant', () => StateObserver.init());
 *
 * The first argument is typed: a name that is not a step does not compile.
 * The second is a function, so the step is open before its work starts. That
 * keeps the whole work inside the duration, and lets a step measured inside
 * another one be told apart from the one around it.
 * The result and the error of the work pass through unchanged.
 *
 * WHAT IT ANSWERS
 * How long the player waited for the game, which launch step took the time,
 * how much was downloaded, and how many players never got in.
 *
 * WHEN THE EVENT IS SENT
 * Once per session, by whichever comes first:
 *
 * - `finished`: the splash is dismissed. The `GCInstant` proxy in
 *   `src/lib/gcinstant.ts` calls `finishGameLoad` before every
 *   `dismissLoadingScreenAsync`, so launch sequences never call it themselves.
 *   `whenGameLoadFinished` resolves at that first dismissal, for session work
 *   that must not compete with the launch.
 * - `timeout`: the load is still running after 20 s of technical time. The
 *   event then names the step the player is stuck on in `pendingPhase`. The
 *   later splash dismissal sends nothing.
 *
 *   navigation start                                      splash dismissed
 *   |-- htmlReadyMs --|- bootMs -|- platformInitMs -|- ... -|- launchSequenceMs -|
 *   |<------------------------------ loadMs ---------------------------------->|
 *
 * TECHNICAL TIME
 * `loadMs` is what the player felt. Every other duration is technical time: a
 * clock that stands still while an onboarding prompt waits for the player
 * (`userWaitMs`) and while the document is hidden (`hiddenMs`). `techLoadMs`
 * is that clock at the moment of the report. Compare releases on `techLoadMs`
 * with `userWaitMs = 0` and `hiddenMs = 0`.
 *
 * WHERE THE NUMBERS COME FROM
 * - `<phase>Ms`: `measureLoadPhase`. A step that runs more than once adds up.
 *   A step that did not run in a launch path is absent from the event, not 0.
 * - `blockedBy`: which of `initialAssets` and `replicantReady` ended last.
 *   They run in parallel, and the launch waits for both.
 * - `htmlReadyMs`, `bootMs`, `sdkMs`, `bundleMs`, `assets*`,
 *   `connectionType`: read from the browser when the event is sent,
 *   `BROWSER_READ_DELAY_MS` after the dismissal so the last Resource Timing
 *   entries have landed. Chrome and WebKit report them differently, see the
 *   browser section below.
 *
 * ADDING A STEP
 * 1. Add `<phase>Ms` to `GameLoadFinish` and the phase to `pendingPhase` in
 *    `analytics.config.ts`.
 * 2. Add the phase to `PHASE_PROPERTY`. It does not compile until step 1 is done.
 * 3. Wrap the await with `measureLoadPhase`.
 * 4. Bump `METRICS_VERSION`, update the property list test, and run
 *    `npm run generate:analytics:doc`.
 *
 * Everything below `measureLoadPhase`, `beginUserWait`, `finishGameLoad` and
 * `whenGameLoadFinished` is exported for the tests only.
 */
import { captureException } from '@sentry/browser';
import type { EventPropertyValues } from '@play-co/analytics-schema';
import type { GameAnalyticsSchema } from '../../../analytics.config';
import { track } from './utils';

/**
 * Bump whenever the meaning of a GameLoadFinish property changes, so charts can
 * tell old and new definitions apart.
 */
const METRICS_VERSION = 3;

/** Technical load time after which the load is reported as timed out. */
const STALL_AFTER_MS = 20 * 1000;

/**
 * Wait between the dismissal and the browser read. The browser queues a
 * Resource Timing entry after each response, behind the work of the dismissal
 * itself. Measured locally: one task later 19 of 58 entries were still
 * missing, 60 ms later none were. A phone is slower, so the wait is generous.
 */
const BROWSER_READ_DELAY_MS = 250;

type GameLoadFinishProperties = EventPropertyValues<
  GameAnalyticsSchema,
  'GameLoadFinish'
>;

/** Event property that carries the duration of each launch step. */
const PHASE_PROPERTY = {
  platformInit: 'platformInitMs',
  initialAssets: 'initialAssetsMs',
  replicantReady: 'replicantReadyMs',
  replicant: 'replicantMs',
  firstScene: 'firstSceneMs',
  friends: 'friendsMs',
  startGame: 'startGameMs',
  entryFinal: 'entryFinalMs',
  afterEntryFinal: 'afterEntryFinalMs',
  sceneEnter: 'sceneEnterMs',
  launchSequence: 'launchSequenceMs',
} as const satisfies Record<string, keyof GameLoadFinishProperties>;

/** Steps of the game load whose duration is reported on GameLoadFinish. */
export type GameLoadPhase = keyof typeof PHASE_PROPERTY;

////////////////////////////////////////////////////////////////////////////////
// Public API
////////////////////////////////////////////////////////////////////////////////

/**
 * Measure one step of the game load. The duration excludes time spent waiting
 * for the player and time the document was hidden. Calls for the same step add
 * up, and calls after the event is sent are ignored.
 *
 * @param phase Step being measured. A name that is not a step does not compile.
 * @param work Starts the work of the step. Called once, right away.
 * @returns The result of the work. An error of the work is rethrown.
 *
 * @example
 * // now
 * await StateObserver.init();
 *
 * // measured: reported as `replicantMs`
 * await measureLoadPhase('replicant', () => StateObserver.init());
 */
export function measureLoadPhase<T>(
  phase: GameLoadPhase,
  work: () => Promise<T>,
): Promise<T> {
  return session.measurePhase(phase, work);
}

/**
 * Stop the load clock while a LINE onboarding prompt waits for the player, so
 * the wait is not counted as load time. Used by `onboarding.sequence.ts` only.
 *
 * @returns Function that starts the clock again. Call it when the prompt closes.
 *
 * @example
 * const endUserWait = beginUserWait();
 * try {
 *   await requestProfileConsent('splash_screen');
 * } finally {
 *   endUserWait();
 * }
 */
export function beginUserWait(): () => void {
  return session.beginUserWait();
}

/**
 * Report the load as finished. Used by the `GCInstant` proxy only, before every
 * `dismissLoadingScreenAsync`. Only the first report of a session is sent.
 *
 * @param launchPath Launch path that dismissed the splash.
 *
 * @example
 * // A new launch path needs nothing more than its dismissal:
 * await platform.dismissLoadingScreenAsync({ launchPath: 'my_new_path' });
 */
export function finishGameLoad(launchPath?: string): void {
  session.finish(launchPath);
}

/**
 * Resolves at the first dismissal of the session, when the splash comes down.
 * Work that would compete with the launch for the main thread and the network
 * waits for this. The stall timeout does not resolve it: a timed out load is
 * still running, so a path that never dismisses needs its own fallback.
 *
 * @example
 * void whenGameLoadFinished().then(() => this.startPostSplashWork());
 */
export function whenGameLoadFinished(): Promise<void> {
  return session.finished;
}

////////////////////////////////////////////////////////////////////////////////
// Technical clock
////////////////////////////////////////////////////////////////////////////////

type PauseReason = 'userWait' | 'hidden';

/**
 * Stopwatch of technical load time. It stands still while the load waits for
 * the player or while the document is hidden, so neither counts against the
 * game. Several reasons can be active at once: the clock runs again only when
 * the last one ends, and overlapping time is never removed twice.
 *
 * @example
 * const clock = new TechClock();
 * // 1000 ms of loading, then a consent prompt opens.
 * clock.pause('userWait');
 * // 4000 ms later the player answers.
 * clock.resume('userWait');
 * // 500 ms more loading.
 * clock.techMs(); // => 1500
 * clock.pausedMs('userWait'); // => 4000
 */
export class TechClock {
  /** Reasons active right now, with the time each one started. */
  private readonly activeSince = new Map<PauseReason, number>();

  /** Finished time per reason. Reasons can overlap, so these do not add up. */
  private readonly finishedMs = new Map<PauseReason, number>();

  /** Finished time with at least one reason active. */
  private stoppedMs = 0;
  private stoppedSince: number | undefined;

  get isRunning(): boolean {
    return this.activeSince.size === 0;
  }

  /** Stop the clock for a reason. Pausing twice for one reason is a no-op. */
  pause(reason: PauseReason): void {
    if (this.activeSince.has(reason)) return;

    const now = performance.now();
    if (this.isRunning) this.stoppedSince = now;
    this.activeSince.set(reason, now);
  }

  /** End a reason. The clock runs again once no reason is left. */
  resume(reason: PauseReason): void {
    const since = this.activeSince.get(reason);
    if (since === undefined) return;

    const now = performance.now();
    this.activeSince.delete(reason);
    this.finishedMs.set(
      reason,
      (this.finishedMs.get(reason) ?? 0) + now - since,
    );

    if (this.isRunning && this.stoppedSince !== undefined) {
      this.stoppedMs += now - this.stoppedSince;
      this.stoppedSince = undefined;
    }
  }

  /** Time since navigation start, minus the time the clock stood still. */
  techMs(): number {
    const now = performance.now();
    const stoppingMs =
      this.stoppedSince === undefined ? 0 : now - this.stoppedSince;

    return now - this.stoppedMs - stoppingMs;
  }

  /** Time one reason was active, including a stretch that is still open. */
  pausedMs(reason: PauseReason): number {
    const since = this.activeSince.get(reason);
    const openMs = since === undefined ? 0 : performance.now() - since;

    return (this.finishedMs.get(reason) ?? 0) + openMs;
  }
}

////////////////////////////////////////////////////////////////////////////////
// Browser timings
////////////////////////////////////////////////////////////////////////////////

/** Fields of a Resource Timing entry that the asset summary reads. */
export type ResourceSizes = Pick<
  PerformanceResourceTiming,
  'name' | 'transferSize' | 'encodedBodySize' | 'responseEnd'
>;

type BrowserProperties = Pick<
  GameLoadFinishProperties,
  | 'htmlReadyMs'
  | 'bootMs'
  | 'sdkMs'
  | 'bundleMs'
  | 'assetsCount'
  | 'assetsCachedCount'
  | 'assetsTransferKb'
  | 'connectionType'
>;

/** Game files: raw assets, and the spritesheets the build packs them into. */
const ASSET_PATHS = ['/assets/', '/spritesheets/'];

/**
 * An asset came over the network when the browser reports both sizes. A cache
 * hit is `transferSize: 0` in Chrome and `encodedBodySize: 0` in WebKit.
 */
function isFromNetwork(entry: ResourceSizes): boolean {
  return entry.transferSize > 0 && entry.encodedBodySize > 0;
}

/**
 * Summarize the asset requests of the load.
 *
 * @param entries Resource Timing entries of the page.
 * @param endedBeforeMs The dismissal, in ms since navigation start. The entries
 *   are read later than that, and background loading starts at once.
 * @returns The asset properties. Empty when no asset was requested.
 *
 * @example
 * summarizeAssets([
 *   { name: '/assets/a.png', transferSize: 5308, encodedBodySize: 5008, responseEnd: 100 }, // network
 *   { name: '/assets/b.png', transferSize: 0, encodedBodySize: 5008, responseEnd: 100 }, // Chrome cache
 *   { name: '/spritesheets/c.avif', transferSize: 300, encodedBodySize: 0, responseEnd: 100 }, // WebKit cache
 *   { name: '/assets/d.png', transferSize: 5308, encodedBodySize: 5008, responseEnd: 900 }, // after the splash
 * ], 500);
 * // => { assetsCount: 3, assetsCachedCount: 2, assetsTransferKb: 5 }
 */
export function summarizeAssets(
  entries: readonly ResourceSizes[],
  endedBeforeMs: number,
): BrowserProperties {
  const assets = entries.filter(
    (entry) =>
      entry.responseEnd <= endedBeforeMs &&
      ASSET_PATHS.some((path) => entry.name.includes(path)),
  );
  if (assets.length === 0) return {};

  // Browsers without body sizes report 0 for both fields on every entry, which
  // cannot be told apart from a fully cached load. Only the count is safe then.
  const hasSizes = assets.some(
    (entry) => entry.transferSize > 0 || entry.encodedBodySize > 0,
  );
  if (!hasSizes) return { assetsCount: assets.length };

  const fromNetwork = assets.filter(isFromNetwork);
  const transferBytes = fromNetwork.reduce(
    (total, entry) => total + entry.encodedBodySize,
    0,
  );

  return {
    assetsCount: assets.length,
    assetsCachedCount: assets.length - fromNetwork.length,
    assetsTransferKb: Math.round(transferBytes / 1024),
  };
}

/**
 * Time from navigation start to the end of the index.html response, and from
 * there to our JS starting to run.
 *
 * Chrome and WebKit agree on `responseEnd` only. After a cross-origin redirect
 * WebKit zeroes `fetchStart`, so the LINE redirect cannot be split from our HTML.
 *
 * @param navigation Navigation Timing entry of the page, when there is one.
 * @param bootedAt When our JS started running, in ms since navigation start.
 * @returns The two durations. Empty when the browser does not report them.
 *
 * @example
 * readNavigation({ responseEnd: 300 }, 500); // => { htmlReadyMs: 300, bootMs: 200 }
 */
export function readNavigation(
  navigation: Pick<PerformanceNavigationTiming, 'responseEnd'> | undefined,
  bootedAt: number,
): BrowserProperties {
  if (!navigation || navigation.responseEnd <= 0) return {};

  return {
    htmlReadyMs: Math.round(navigation.responseEnd),
    bootMs: Math.round(Math.max(bootedAt - navigation.responseEnd, 0)),
  };
}

/** Fields of a Resource Timing entry that the boot split reads. */
export type ResourceTimes = Pick<
  PerformanceResourceTiming,
  'name' | 'startTime' | 'responseEnd' | 'duration'
>;

const LIFF_SDK_PATH = '/liff/';
const BUNDLE_PREFIX = 'timestep-instant-';

/**
 * The two downloads inside `bootMs`: the LIFF SDK and the main bundle.
 *
 * `liff.init` runs between them in the bootstrap but cannot be read here: the
 * page preloads the bundle with `modulepreload`, so its request starts during
 * the HTML parse, before the SDK response ends. The bootstrap measures
 * `liff.init` itself and does not expose it yet.
 *
 * The SDK is cross-origin without `Timing-Allow-Origin`, which leaves
 * `startTime`, `responseEnd` and `duration` readable and zeroes the rest.
 *
 * @param entries Resource Timing entries of the page.
 * @returns The two durations. Each one is absent when its entry is missing.
 *
 * @example
 * readBoot([
 *   { name: 'https://static.line-scdn.net/liff/edge/versions/2.29.2/sdk.js', startTime: 400, responseEnd: 700, duration: 300 },
 *   { name: 'https://buruburu.app/timestep-instant-abc.js', startTime: 20, responseEnd: 1300, duration: 1280 },
 * ]);
 * // => { sdkMs: 300, bundleMs: 1280 }
 */
export function readBoot(entries: readonly ResourceTimes[]): BrowserProperties {
  const sdk = entries.find(
    (entry) =>
      entry.name.includes(LIFF_SDK_PATH) && entry.name.endsWith('sdk.js'),
  );
  const bundle = entries.find((entry) =>
    entry.name.slice(entry.name.lastIndexOf('/') + 1).startsWith(BUNDLE_PREFIX),
  );

  return {
    ...(sdk && { sdkMs: Math.round(sdk.duration) }),
    ...(bundle && { bundleMs: Math.round(bundle.duration) }),
  };
}

/**
 * Read the browser. The functions above work on plain data and are tested so.
 *
 * @param bootedAt When our JS started running, in ms since navigation start.
 * @param dismissedAt The dismissal, in ms since navigation start.
 */
function readBrowser(bootedAt: number, dismissedAt: number): BrowserProperties {
  // Network Information API, Chromium only.
  const { connection } = navigator as Navigator & {
    connection?: { effectiveType?: string };
  };
  const connectionType = connection?.effectiveType;

  // A browser without the timing APIs still reports the rest of the event.
  if (typeof performance.getEntriesByType !== 'function') {
    return { connectionType };
  }

  const [navigation] = performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];
  const resources = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[];

  return {
    ...readNavigation(navigation, bootedAt),
    ...readBoot(resources),
    ...summarizeAssets(resources, dismissedAt),
    connectionType,
  };
}

////////////////////////////////////////////////////////////////////////////////
// Tracker
////////////////////////////////////////////////////////////////////////////////

type GameLoadOutcome = GameLoadFinishProperties['outcome'];

type PhaseProperties = Partial<
  Record<(typeof PHASE_PROPERTY)[GameLoadPhase], number>
>;

type OpenPhase = {
  phase: GameLoadPhase;
  startedTechMs: number;
};

/**
 * Tracks one game load and reports it once. Game code uses the functions at
 * the top of this file, which share the tracker of the session. A test builds
 * its own on Jest fake timers.
 */
export class GameLoadTracker {
  private readonly clock = new TechClock();

  /** Steps running right now, oldest first. */
  private readonly openPhases: OpenPhase[] = [];
  /** Every closed step as [start, end] in technical time, for `unmeasuredMs`. */
  private readonly intervals: Array<[number, number]> = [];
  private readonly phaseDurations = new Map<GameLoadPhase, number>();
  private readonly phaseEndedAt = new Map<GameLoadPhase, number>();

  private isStarted = false;
  private isSent = false;
  private stallTimer: ReturnType<typeof setTimeout> | undefined;

  private resolveFinished = () => {};
  /** Resolves at the first `finish`, whether or not it is the one reported. */
  readonly finished = new Promise<void>((resolve) => {
    this.resolveFinished = resolve;
  });

  /** @param bootedAt When our JS started running, in ms since navigation start. */
  constructor(private readonly bootedAt = performance.now()) {}

  async measurePhase<T>(
    phase: GameLoadPhase,
    work: () => Promise<T>,
  ): Promise<T> {
    // A step measured after the report, such as a later puzzle, is not game load.
    if (this.isSent) return work();

    this.start();
    const open = { phase, startedTechMs: this.clock.techMs() };
    this.openPhases.push(open);

    // The step is open before its work starts, so a step measured inside the
    // work opens after it and is the innermost one.
    try {
      return await work();
    } finally {
      // A step the report already cut short did not end here.
      if (this.closePhase(open)) {
        this.phaseEndedAt.set(phase, performance.now());
      }
    }
  }

  beginUserWait(): () => void {
    if (this.isSent) return () => {};

    this.start();
    this.clock.pause('userWait');
    this.scheduleStall();

    return () => {
      this.clock.resume('userWait');
      this.scheduleStall();
    };
  }

  setHidden(isHidden: boolean): void {
    if (this.isSent) return;

    if (isHidden) this.clock.pause('hidden');
    else this.clock.resume('hidden');
    this.scheduleStall();
  }

  finish(launchPath?: string): void {
    this.report('finished', launchPath);
    this.resolveFinished();
  }

  /**
   * The stall timer only runs once the launch has started measuring, so a page
   * that merely imports this module never reports a timeout.
   */
  private start(): void {
    if (this.isStarted) return;
    this.isStarted = true;
    this.scheduleStall();
  }

  /**
   * The timer runs only while the technical clock does, so when it fires the
   * load has used exactly `STALL_AFTER_MS` of technical time.
   */
  private scheduleStall(): void {
    clearTimeout(this.stallTimer);
    if (!this.isStarted || this.isSent || !this.clock.isRunning) return;

    const remainingMs = Math.max(STALL_AFTER_MS - this.clock.techMs(), 0);
    this.stallTimer = setTimeout(() => this.report('timeout'), remainingMs);
  }

  /** @returns Whether the step was still open. */
  private closePhase(open: OpenPhase): boolean {
    const index = this.openPhases.indexOf(open);
    if (index === -1) return false;
    this.openPhases.splice(index, 1);

    // A step entered more than once, such as startGame, adds up.
    const endedTechMs = this.clock.techMs();
    const durationMs = endedTechMs - open.startedTechMs;
    const previousMs = this.phaseDurations.get(open.phase) ?? 0;
    this.phaseDurations.set(open.phase, previousMs + durationMs);
    this.intervals.push([open.startedTechMs, endedTechMs]);

    return true;
  }

  private report(outcome: GameLoadOutcome, launchPath?: string): void {
    if (this.isSent) return;
    this.isSent = true;
    clearTimeout(this.stallTimer);

    // Analytics must never prevent the game from dismissing the splash.
    try {
      // The innermost step still running is what a timed out player is stuck on.
      const pendingPhase =
        outcome === 'timeout' ? this.getInnermostOpenPhase() : undefined;
      // Steps still running end here, such as the launch sequence itself.
      for (const open of [...this.openPhases]) this.closePhase(open);

      // Every duration is taken now, at the dismissal.
      const dismissedAt = performance.now();
      const properties: GameLoadFinishProperties = {
        ...this.getProperties(outcome),
        pendingPhase,
        launchPath,
      };
      // `sessionElapsedTime` overrides the default event property with the
      // onboarding-adjusted load duration in seconds. The charts built before
      // the other properties existed rely on it. It is a default property of
      // every event, so the schema does not list it.
      const onboardingAdjustedMs =
        performance.now() - this.clock.pausedMs('userWait');
      const sessionElapsedTime = Math.round(onboardingAdjustedMs) / 1000;

      // The last assets end right before the dismissal and their Resource
      // Timing entries land after it. Only the browser properties wait, and
      // only entries that ended before the dismissal count.
      setTimeout(() => {
        try {
          // A non-literal object: `sessionElapsedTime` is not in the schema.
          const event = {
            ...properties,
            ...readBrowser(this.bootedAt, dismissedAt),
            sessionElapsedTime,
          };
          track('GameLoadFinish', event);
        } catch (error) {
          captureException(error);
        }
      }, BROWSER_READ_DELAY_MS);
    } catch (error) {
      captureException(error);
    }
  }

  private getProperties(outcome: GameLoadOutcome): GameLoadFinishProperties {
    return {
      metricsVersion: METRICS_VERSION,
      outcome,
      loadMs: Math.round(performance.now()),
      techLoadMs: Math.round(this.clock.techMs()),
      userWaitMs: Math.round(this.clock.pausedMs('userWait')),
      hiddenMs: Math.round(this.clock.pausedMs('hidden')),
      blockedBy: this.getBlockedBy(),
      unmeasuredMs: this.getUnmeasuredMs(),
      ...this.getPhaseProperties(),
    };
  }

  /**
   * Technical time from the first step to the report that no step covers: the
   * sync code between the awaits, view creation, `createSystems`. Steps nest
   * and overlap, so their intervals are merged before subtracting.
   */
  private getUnmeasuredMs(): number | undefined {
    if (this.intervals.length === 0) return undefined;

    const sorted = [...this.intervals].sort((a, b) => a[0] - b[0]);
    const firstStart = sorted[0][0];
    let coveredMs = 0;
    let [start, end] = sorted[0];
    for (const [nextStart, nextEnd] of sorted.slice(1)) {
      if (nextStart > end) {
        coveredMs += end - start;
        [start, end] = [nextStart, nextEnd];
      } else {
        end = Math.max(end, nextEnd);
      }
    }
    coveredMs += end - start;

    return Math.round(this.clock.techMs() - firstStart - coveredMs);
  }

  private getInnermostOpenPhase(): GameLoadPhase | undefined {
    return this.openPhases[this.openPhases.length - 1]?.phase;
  }

  /** The launch waits for both parallel branches. The later one blocked it. */
  private getBlockedBy(): GameLoadFinishProperties['blockedBy'] {
    const assetsEndedAt = this.phaseEndedAt.get('initialAssets');
    const replicantEndedAt = this.phaseEndedAt.get('replicantReady');
    if (assetsEndedAt === undefined || replicantEndedAt === undefined) {
      return undefined;
    }

    return assetsEndedAt > replicantEndedAt ? 'assets' : 'replicant';
  }

  /** A step that did not run in this launch path is absent, not zero. */
  private getPhaseProperties(): PhaseProperties {
    const properties: PhaseProperties = {};
    for (const [phase, durationMs] of this.phaseDurations) {
      properties[PHASE_PROPERTY[phase]] = Math.round(durationMs);
    }

    return properties;
  }
}

////////////////////////////////////////////////////////////////////////////////
// The load of this session
////////////////////////////////////////////////////////////////////////////////

/** A page loads the game once. This module is evaluated with the bundle. */
const session = new GameLoadTracker();

session.setHidden(document.visibilityState === 'hidden');
document.addEventListener('visibilitychange', () => {
  session.setHidden(document.visibilityState === 'hidden');
});
